/**
 * SentenceController — manages the sentence playback queue.
 *
 * Sentences arrive from the server in two parts:
 *   1. Metadata (text, isLast, animName) via onTextOut → addSentenceMeta()
 *   2. Animation frames via onAudioAnimation / onStreamChunk → addFrames() / addStreamChunk()
 *
 * Playback is MANUAL: nothing plays until playFirst() or playNext() is called.
 * Default Event Graph wiring replicates auto-play:
 *   onNewResponse → PlayFirstSentence
 *   onSentenceCompleted → PlayNextSentence
 *
 * Callbacks (set by App.jsx → EventRuntime.fireEvent):
 *   onGesture(data)            — VoiceText.Animation
 *   onSentenceStarted(data)    — VoiceText.Audio.Started
 *   onSentenceCompleted(data)  — VoiceText.Data.SentenceCompleted
 *   onLastSentenceStarted(data)— VoiceText.Data.LastSentenceStarted
 *   onAllSentencesPlayed()     — VoiceText.Data.LastSentenceCompleted
 *   onSentenceDataReady(data)  — a sentence is ready to PLAY ({ sentenceNum,
 *                                isLast }); fires per sentence, out of order,
 *                                once each. "Ready" means audio has landed
 *                                (`terminated`) AND the sentence's TextOut has
 *                                arrived (`hasMeta`) — see _signalDataReady
 */
import { audioPlayer } from '../network/AudioPlayer'
import { tracePhase } from './perfProbe'
import { devLog } from './devLog'
import { clearGestureQueue } from './gestureSequencer.js'
import { noteSentenceDelivery } from './a2fHealth.js'

class SentenceController {
  constructor() {
    this._queue   = []     // [{ sentenceNum, text, isLast, animName, animAction, tracks, frames, framesReady, hasAudio }]
    this._current = -1     // index into _queue currently playing
    this._buffer  = null   // AudioAnimationBuffer ref
    this._waitTimer = null // setInterval waiting for frames to arrive

    // Callbacks — wire these in App.jsx
    this.onGesture             = null
    this.onSentenceStarted     = null
    this.onSentenceCompleted   = null
    this.onLastSentenceStarted = null
    this.onAllSentencesPlayed  = null
    this.onSentenceWaiting     = null
    this.onSentenceDataReady   = null
    // Fires exactly once per response, when the OPENING sentence (sentenceNum 0)
    // becomes ready to play — the same terminated + hasMeta AND-join as
    // onSentenceDataReady, filtered to the first sentence. "First" is the
    // playback-order first (sentenceNum 0), NOT whichever sentence terminates
    // first: sentence 2 can be ready earlier, but this only fires for 0. Use it
    // to kick off "avatar is about to start speaking" logic. onSentenceDataReady
    // ALSO fires for sentence 0 — this is an additional convenience event, not a
    // replacement.
    this.onFirstSentenceReady  = null

    // Secondary observer for Voxy integration — signals avatar speaking state
    this.onAvatarSpeakingChange = null  // (speaking: boolean) => void
  }

  /** Call once after AudioAnimationBuffer is created. */
  setBuffer(buffer) {
    this._buffer = buffer
  }

  /** Called on onNewResponse — discard previous response's queue. */
  reset() {
    this._clearWait()
    this._queue   = []
    this._current = -1
    // Pending (not-yet-started) sequenced gestures belong to the discarded
    // response — a new response or a barge-in must not inherit the backlog.
    // The currently PLAYING clip keeps its own channel's teardown semantics.
    clearGestureQueue()
    audioPlayer.reset()
    this.onAvatarSpeakingChange?.(false)
    if (this._buffer) {
      this._buffer.onComplete = null
      this._buffer.clear()
    }
  }

  /**
   * Called when a TextOut message arrives (metadata only, no frames yet).
   * Fires onGesture immediately if the sentence carries an animation tag.
   */
  addSentenceMeta(sentenceNum, { text = '', isLast = false, animName = '', animAction = '', tracks = undefined, motionClass = undefined, animations = undefined } = {}) {
    tracePhase('sc.addSentenceMeta', () => {
      // `animations` is the FULL per-sentence list off TextOut — convorcher can
      // attach several (each its own generated clip). The single animName/
      // animAction/tracks/motionClass args remain as the [0] view for callers
      // and tests that predate the list; when both are given, `animations`
      // wins. Each item: { animName, actionText, tracks, motionClass }.
      const anims = (animations && animations.length)
        ? animations
        : (animName ? [{ animName, actionText: animAction, tracks, motionClass }] : [])
      const first = anims[0] ?? { animName: '', actionText: '', tracks: undefined, motionClass: undefined }

      let entry = this._queue.find(s => s.sentenceNum === sentenceNum)
      const meta = {
        text, isLast,
        // `hasMeta` marks that this sentence's TextOut has been applied — the
        // gate _signalDataReady uses to (a) trust `isLast` and (b) reject a
        // post-reset straggler, which recreates a metadata-less entry via the
        // frames path and never receives a TextOut (see _signalDataReady).
        hasMeta: true,
        animName:    first.animName,
        animAction:  first.actionText,
        tracks:      first.tracks,
        motionClass: first.motionClass,
        animations:  anims,
      }
      if (!entry) {
        entry = { sentenceNum, ...meta, frames: null, framesReady: false }
        this._queue.push(entry)
        this._queue.sort((a, b) => a.sentenceNum - b.sentenceNum)
      } else {
        Object.assign(entry, meta)
      }

      // Metadata just landed. If this sentence's audio already terminated (the
      // frames path ran first — abnormal ordering, but the AND-join must not
      // lose the event), both gates are now satisfied and it fires here.
      // Normal ordering (TextOut before frames) makes this a no-op; the event
      // then fires later, on the audio-bearing `done` chunk.
      this._signalDataReady(entry)

      if (anims.length) {
        // One onGesture per animation, in server order. `tracks` is the raw
        // kimodo payload when convorcher served a GENERATED gesture inline on
        // TextOut; undefined for a stock gestures.json tag. `motionClass`
        // rides alongside it — 'performance' routes to the override channel in
        // EventRuntime's playGesture handler, absent/anything else means the
        // additive path. Ordering of the generated clips themselves is
        // enforced downstream by
        // gestureSequencer (the playGesture node enqueues; first starts
        // synchronously here, the rest chain by clip duration).
        for (const a of anims) {
          if (!a.animName) continue
          tracePhase('sc.onGesture', () =>
            this.onGesture?.({ animName: a.animName, actionText: a.actionText, sentenceNum, tracks: a.tracks, motionClass: a.motionClass }))
        }
      }
    })
  }

  /**
   * Called when AudioAnimation message arrives (non-streaming, all frames + audio at once).
   * @param {number}     sentenceNum
   * @param {any[]}      frames      blendshape frames
   * @param {Uint8Array} [audio]     deflate-raw compressed PCM (may be absent)
   */
  addFrames(sentenceNum, frames, audio) {
    if (!frames || frames.length === 0) return
    let entry = this._queue.find(s => s.sentenceNum === sentenceNum)
    if (!entry) {
      entry = { sentenceNum, text: '', isLast: false, animName: '', animAction: '', tracks: undefined, motionClass: undefined, frames: null, framesReady: false, hasAudio: false }
      this._queue.push(entry)
      this._queue.sort((a, b) => a.sentenceNum - b.sentenceNum)
    }
    // See _addStreamChunkImpl: the first audio-bearing rendering per sentence
    // wins; a later duplicate (two-tier TTS) would double-play. Drop it.
    if (entry.terminated) {
      devLog(`[SentenceController] drop duplicate batch n=${sentenceNum} (audio=${audio?.length ?? 0}B) — sentence already finalized`)
      return
    }
    entry.frames = frames
    entry.framesReady = true

    if (audio && audio.length > 0) {
      entry.hasAudio = true
      entry.terminated = true
      audioPlayer.addBatch(sentenceNum, audio)
    }

    // Batch path: same A2F liveness check as the streaming path above. (This
    // early-returns on an empty `frames`, so reaching here means frames > 0 —
    // recorded anyway so recovery clears a fault raised by the streaming path.)
    noteSentenceDelivery({
      sentenceNum,
      hasAudio: !!entry.hasAudio,
      frameCount: Array.isArray(frames) ? frames.length : 0,
    })

    // Frames are ready → this sentence's data is ready.
    this._signalDataReady(entry)

    // If we're currently waiting for this sentence → unblock
    if (this._current >= 0 && this._queue[this._current]?.sentenceNum === sentenceNum) {
      this._clearWait()
      this._startPlaying(this._current)
    }
  }

  /**
   * Called for each streaming chunk. Accumulates frames; passes audio to AudioPlayer.
   */
  addStreamChunk(msg) {
    tracePhase(`sc.addStreamChunk.${msg.frames?.length ?? 0}f.${msg.audio?.length ?? 0}B`, () =>
      this._addStreamChunkImpl(msg))
  }

  _addStreamChunkImpl(msg) {
    const { sentenceNum, frames, audio, done } = msg
    let entry = this._queue.find(s => s.sentenceNum === sentenceNum)
    if (!entry) {
      entry = { sentenceNum, text: '', isLast: false, animName: '', animAction: '', tracks: undefined, motionClass: undefined, frames: [], framesReady: false, hasAudio: false }
      this._queue.push(entry)
      this._queue.sort((a, b) => a.sentenceNum - b.sentenceNum)
    }
    // Idempotency guard. Convorcher's two-tier TTS emits TWO terminal (done)
    // StreamChunks per sentence — a fast draft, then a higher-quality render —
    // both carrying complete audio for the same sentenceNum. Scheduling the
    // second one plays the sentence twice (overlapping audio). The first
    // terminal chunk wins; everything after it for this sentence is dropped.
    // (The correct source fix is convorcher not double-emitting; this keeps the
    // client correct regardless — a client must never double-play on a
    // redundant server message.)
    if (entry.terminated) {
      devLog(`[SentenceController] drop post-terminal chunk n=${sentenceNum} (done=${!!done}, audio=${audio?.length ?? 0}B) — sentence already finalized`)
      return
    }
    if (!Array.isArray(entry.frames)) entry.frames = []
    if (frames && frames.length > 0) {
      for (const f of frames) entry.frames.push(f)
    }
    if (audio && audio.length > 0) {
      entry.hasAudio = true
      tracePhase('audio.addChunk', () => audioPlayer.addChunk(sentenceNum, audio, !!done))
    }
    if (done) {
      entry.framesReady = true
      // Finalize the sentence only once THIS rendering has actually delivered
      // audio. The server sends multiple `done` chunks per sentence — often an
      // empty marker (audio=0B) before the real audio, then a fast draft and a
      // quality render. Terminating on the empty marker would drop the only
      // audio (silent sentence); terminating on the first audio-bearing `done`
      // keeps that rendering and drops the redundant later one (no double).
      if (entry.hasAudio) entry.terminated = true
      // Audio delivered but zero frames accumulated = A2F produced nothing for
      // this sentence. Reported (not silenced) so an A2F outage doesn't present
      // as a frozen-face avatar bug — see a2fHealth.js.
      noteSentenceDelivery({
        sentenceNum,
        hasAudio: !!entry.hasAudio,
        frameCount: Array.isArray(entry.frames) ? entry.frames.length : 0,
      })
      // Frames are ready → this sentence's data is ready. The once-per-entry
      // guard makes the redundant later `done` chunks (two-tier TTS) no-ops.
      this._signalDataReady(entry)
      if (this._current >= 0 && this._queue[this._current]?.sentenceNum === sentenceNum) {
        this._clearWait()
        tracePhase('startPlaying', () => this._startPlaying(this._current))
      }
    }
  }

  /** Start playing sentence at queue index 0. */
  playFirst() {
    this._playSentence(0)
  }

  /** Start playing the next sentence in the queue. */
  playNext() {
    this._playSentence(this._current + 1)
  }

  /**
   * The first/last raw ARKit frame (52 blendshape weights) of a queued sentence,
   * for the Stream First/Last Frame event actions to publish as a static face
   * pose. `sentenceNum` targets a specific sentence; when omitted, 'first'
   * defaults to the NEXT sentence to play (queue slot after the cursor — which
   * is sentence 0 before anything has played) and 'last' to the CURRENT sentence.
   * Returns null when that sentence isn't queued or its frames haven't arrived.
   */
  getStreamFrame(which, sentenceNum = null) {
    const entry = sentenceNum != null
      ? this._queue.find(s => s.sentenceNum === sentenceNum)
      : (which === 'last' ? this._queue[this._current] : this._queue[this._current + 1])
    const frames = entry?.frames
    if (!frames || frames.length === 0) return null
    const f = which === 'last' ? frames[frames.length - 1] : frames[0]
    return f?.blendshapeWeights ?? null
  }

  // ── Internal ────────────────────────────────────────────────────────────────

  _playSentence(index) {
    this._current = index
    this._clearWait()

    const tryStart = () => {
      const entry = this._queue[index]
      if (entry?.framesReady) {
        this._clearWait()
        this._startPlaying(index)
        return true
      }
      return false
    }

    if (!tryStart()) {
      this.onSentenceWaiting?.({})
      this._waitTimer = setInterval(tryStart, 50)
    }
  }

  _startPlaying(index) {
    const entry = this._queue[index]
    if (!entry || !this._buffer) return

    // A sentence is started exactly once. Guards the wait-timer / late-chunk
    // races that could otherwise re-enter _startPlaying for a sentence already
    // playing (the double/overlapping-audio bug).
    if (entry.started) {
      devLog(`[SentenceController] _startPlaying ignored — n=${entry.sentenceNum} already started`)
      return
    }
    entry.started = true

    this._buffer.clear()
    this._buffer.onComplete = null
    this._buffer.push({ frames: entry.frames, sentenceNum: entry.sentenceNum })

    this.onSentenceStarted?.({ sentenceNum: entry.sentenceNum, text: entry.text, animName: entry.animName, actionText: entry.animAction })
    this.onAvatarSpeakingChange?.(true)
    if (entry.isLast) {
      this.onLastSentenceStarted?.({ sentenceNum: entry.sentenceNum })
    }

    const onDone = () => {
      this.onSentenceCompleted?.({ sentenceNum: entry.sentenceNum, isLast: !!entry.isLast })
      if (entry.isLast) {
        this.onAvatarSpeakingChange?.(false)
        this.onAllSentencesPlayed?.()
      }
    }

    if (entry.hasAudio) {
      // Audio drives completion — more reliable than frame timing
      audioPlayer.startSentence(entry.sentenceNum, onDone)
    } else {
      // No audio — fall back to face animation frame completion
      this._buffer.onComplete = onDone
    }
  }

  /**
   * Fire onSentenceDataReady for a sentence that has become ready to PLAY.
   * Per sentence, with no ordering — sentence 2 can fire before sentence 1.
   * Consumers own any ordering/play mapping downstream.
   *
   * Two gates, both required — this is an AND-join, so it's called from every
   * point that can flip either gate (addSentenceMeta, addFrames, the streaming
   * `done`) and only fires once both hold:
   *
   *  - `terminated` — the sentence's audio-bearing `done` has landed, so every
   *    chunk for this sentence is in and a play started now picks up audio +
   *    frames with NO wait. NOT `framesReady`: the two-tier TTS sends an empty
   *    `done` marker (audio=0B) before the real audio that flips framesReady but
   *    not terminated, so a framesReady gate would fire before audio and a play
   *    off it would still stall (ENG-833). The A2F-outage case (audio, zero
   *    frames) still terminates — we want to play audio + neutral face, not
   *    wedge on frames that will never come.
   *  - `hasMeta` — the sentence's TextOut has been applied, so `isLast` is the
   *    real server value (not the frames-path placeholder `false`) AND this is
   *    not a post-reset straggler. A barge-in's reset() wipes the queue; a late
   *    chunk from the discarded response recreates a metadata-less entry via the
   *    frames path, and its TextOut was thrown away with the old queue — so it
   *    never gets `hasMeta` and never fires here. This is the response-epoch
   *    intent (drop stragglers) achieved without a server response id, which the
   *    protocol doesn't carry (`id` is per-message, not per-response).
   *
   * The once-per-entry guard fires exactly once per sentence: the two-tier TTS
   * sends several `done` chunks, but "ready to play" is a single transition. The
   * flags live on the queue entry, which reset() discards per response, so the
   * whole thing re-arms each reply.
   */
  _signalDataReady(entry) {
    if (!entry || entry.dataReadySignalled) return
    if (!entry.terminated || !entry.hasMeta) return  // AND-join: wait for both
    entry.dataReadySignalled = true
    const payload = { sentenceNum: entry.sentenceNum, isLast: !!entry.isLast }
    this.onSentenceDataReady?.(payload)
    // The opening sentence becoming ready is the "start speaking" signal. The
    // once-per-entry guard above + reset()'s fresh queue mean sentence 0's entry
    // reaches here exactly once per response, so no extra flag is needed.
    if (entry.sentenceNum === 0) this.onFirstSentenceReady?.(payload)
  }

  _clearWait() {
    if (this._waitTimer !== null) {
      clearInterval(this._waitTimer)
      this._waitTimer = null
    }
  }
}

export const sentenceController = new SentenceController()
